import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { WARMUP } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Flame, PlayCircle, RefreshCw, MoonStar, Sunrise, Sun,
  ChevronRight, Zap, Clock, CheckCircle2, Circle, ArrowRight, X, Check,
} from "lucide-react";

const rhythmIcon = { night_owl: MoonStar, early_bird: Sunrise, balanced: Sun };
const actionLabel = {
  swipe: "свайп",
  like: "лайк",
  follow: "подписка",
  watch: "смотрит",
  comment: "коммент",
};

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Warmup() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticking, setTicking] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.warmupOverview();
      setRows(d.accounts || []);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tick = async () => {
    setTicking(true);
    try {
      const r = await api.warmupTick(60);
      toast.success(t.warmup.executed(r.executed, r.accounts_touched));
      await load();
      if (drawer) await openPlan(drawer);
    } catch { toast.error(t.common.failed); }
    finally { setTicking(false); }
  };

  const openPlan = async (accountId) => {
    setDrawer(accountId);
    setPlanLoading(true);
    try {
      let d = await api.warmupPlan(accountId);
      if (!d.actions || d.actions.length === 0) {
        await api.createWarmupPlan(accountId);
        d = await api.warmupPlan(accountId);
      }
      setPlan(d);
    } catch { toast.error(t.common.failed); }
    finally { setPlanLoading(false); }
  };

  const regen = async (accountId) => {
    try {
      await api.createWarmupPlan(accountId, true);
      toast.success(t.warmup.regenerated);
      if (drawer === accountId) await openPlan(accountId);
      await load();
    } catch { toast.error(t.common.failed); }
  };

  const advanceDay = async (accountId) => {
    try {
      const r = await api.warmupAdvanceDay(accountId);
      toast.success(t.warmup.advancedTo(r.warmup_day, r.stage === "active"));
      await regen(accountId);
    } catch { toast.error(t.common.failed); }
  };

  const completeAction = async (id) => {
    try { await api.completeWarmupAction(id); if (drawer) await openPlan(drawer); load(); }
    catch { toast.error(t.common.failed); }
  };
  const skipAction = async (id) => {
    try { await api.skipWarmupAction(id); if (drawer) await openPlan(drawer); load(); }
    catch { toast.error(t.common.failed); }
  };
  const deleteAction = async (id) => {
    try { await api.deleteWarmupAction(id); toast.success(t.warmup.deleted); if (drawer) await openPlan(drawer); load(); }
    catch { toast.error(t.common.failed); }
  };

  return (
    <div data-testid={WARMUP.root}>
      <PageHeader
        title={t.warmup.title}
        description={t.warmup.description}
        actions={
          <button
            data-testid={WARMUP.tickBtn}
            onClick={tick}
            disabled={ticking}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {ticking ? <RefreshCw className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            {ticking ? t.warmup.ticking : t.warmup.tick}
          </button>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : rows.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Flame className="h-10 w-10 text-emerald-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.warmup.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.warmup.emptyHint}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {rows.map((r) => {
            const Rhythm = rhythmIcon[r.rhythm] || Sun;
            const progressPct = Math.min(100, Math.round((r.warmup_day / 7) * 100));
            const doneRatio = r.total ? Math.round((r.done / r.total) * 100) : 0;
            return (
              <div key={r.account_id} data-testid={WARMUP.card(r.account_id)} className="card-soft p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display font-semibold text-slate-900 truncate">{r.username}</div>
                      <StatusBadge status={r.stage} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1"><Rhythm className="h-3.5 w-3.5" /> {t.personas.rhythm[r.rhythm] || r.rhythm}</span>
                      <span>гео: {r.geo}</span>
                      <span>health: {r.health_score}%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => openPlan(r.account_id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-700 text-xs font-medium transition-colors"
                  >
                    {t.warmup.plan} <ChevronRight className="h-3 w-3" />
                  </button>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-slate-500">{t.warmup.day} {r.warmup_day} / 7</span>
                    <span className="text-slate-700 font-medium">{progressPct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <MiniStat label={t.warmup.counters.planned} value={r.planned} tone="slate" />
                  <MiniStat label={t.warmup.counters.done} value={r.done} tone="emerald" />
                  <MiniStat label={t.warmup.counters.skipped} value={r.skipped} tone="amber" />
                </div>

                <div className="mt-3 text-xs text-slate-500 flex items-center justify-between">
                  <span>{t.warmup.todayProgress}: <span className="font-medium text-slate-700">{doneRatio}%</span></span>
                  {r.next_action && (
                    <span className="inline-flex items-center gap-1 text-slate-600">
                      <Clock className="h-3 w-3" /> {t.warmup.next(actionLabel[r.next_action.action] || r.next_action.action, fmtTime(r.next_action.planned_at))}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    data-testid={WARMUP.regenBtn(r.account_id)}
                    onClick={() => regen(r.account_id)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> {t.warmup.regen}
                  </button>
                  <button
                    data-testid={WARMUP.advanceBtn(r.account_id)}
                    onClick={() => advanceDay(r.account_id)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-medium transition-colors"
                  >
                    <ArrowRight className="h-3.5 w-3.5" /> {t.warmup.advance}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!drawer} onOpenChange={(o) => { if (!o) { setDrawer(null); setPlan(null); } }}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Zap className="h-4 w-4 text-emerald-500" /> {t.warmup.todaysPlan}
            </DialogTitle>
          </DialogHeader>
          {planLoading ? (
            <div className="text-slate-400 text-sm">{t.common.loading}</div>
          ) : plan ? (
            <>
              <div className="text-xs text-slate-500 mb-2">
                {plan.count} действий · по времени
              </div>
              <div className="overflow-y-auto pr-1 space-y-1 -mx-1 px-1">
                {plan.actions.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                    {a.status === "done" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    ) : a.status === "skipped" ? (
                      <Circle className="h-4 w-4 text-amber-400 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-slate-300 shrink-0" />
                    )}
                    <div className="font-mono text-xs text-slate-500 w-14 shrink-0">{fmtTime(a.planned_at)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900">{actionLabel[a.action] || a.action}</div>
                      <div className="text-[11px] text-slate-500 truncate">{a.note}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      a.status === "done" ? "bg-emerald-100 text-emerald-700" :
                      a.status === "skipped" ? "bg-amber-100 text-amber-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{t.warmup.actionStatus[a.status] || a.status}</span>
                    {a.status === "planned" && (
                      <>
                        <button
                          onClick={() => completeAction(a.id)}
                          className="h-6 w-6 rounded bg-emerald-50 hover:bg-emerald-500 hover:text-white text-emerald-600 flex items-center justify-center transition-colors"
                          title={t.warmup.markDone}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => skipAction(a.id)}
                          className="h-6 w-6 rounded bg-amber-50 hover:bg-amber-500 hover:text-white text-amber-600 flex items-center justify-center transition-colors"
                          title={t.warmup.skip}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {plan.actions.length === 0 && (
                  <div className="text-slate-400 text-sm py-6 text-center">Пусто</div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const cls = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  }[tone];
  return (
    <div className={`rounded-xl px-2 py-2 ${cls}`}>
      <div className="font-display text-xl font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest">{label}</div>
    </div>
  );
}
